/**
 * Post-Onboarding Recipe Carousel
 *
 * Displays 6 personalized recipes matched to user's TempTaste profile.
 * Shows confidence scores and encourages cooking action.
 */

import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Skeleton } from "../../components/ui/skeleton";
import { useAction } from "wasp/client/operations";
import { getInitialRecipeRecommendations } from "wasp/client/operations";
import { AlertCircle, ChefHat } from "lucide-react";

export interface PostOnboardingRecipeCarouselProps {
  formData?: any; // Taste profile preferences from Steps 1-5
  selectedRecipeIds?: string[]; // Track which 3 recipes are selected
  onRecipeSelect?: (recipeId: string) => void; // Callback when recipe is selected/deselected
  onSwipeClick?: (recipeIds: string[]) => void;
  onDiscoverClick?: (recipeIds: string[]) => void;
}

export const PostOnboardingRecipeCarousel: React.FC<
  PostOnboardingRecipeCarouselProps
> = ({ formData, selectedRecipeIds = [], onRecipeSelect, onSwipeClick, onDiscoverClick }) => {
  const [recipes, setRecipes] = React.useState<any[] | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const getRecipes = useAction(getInitialRecipeRecommendations);
  const hasFetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    (async () => {
      try {
        setIsLoading(true);
        // Get initial recommendations using onboarding preferences
        const data = await getRecipes({ formData: formData || {} });
        setRecipes(data as any[]);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getRecipes]);

  // Calculate average confidence
  const avgConfidence =
    recipes && recipes.length > 0
      ? Math.round(
          recipes.reduce((sum, r) => sum + r.matchConfidence, 0) /
            recipes.length,
        )
      : 0;

  // Loading State
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">🎯 Recipes Made For You</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Based on your just-selected flavor preferences
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-24 w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error State
  if (error || !recipes || recipes.length === 0) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Recipes Not Yet Available
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-amber-900">
            We'll generate personalized recipes once your profile is ready.
          </p>
          <Button
            onClick={(e) => {
              e.preventDefault();
              onDiscoverClick?.([]);
            }}
            variant="outline"
            size="sm"
            className="w-full"
          >
            🔍 Browse All Recipes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200/50 bg-gradient-to-br from-green-50/30 to-transparent">
      <CardHeader>
        <div className="space-y-2">
          <CardTitle className="text-base">🎯 Recipes Made For You</CardTitle>
          <p className="text-xs text-muted-foreground">
            Based on your just-selected flavor preferences
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Selection Counter */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            Select your top 3 recipes
          </span>
          <span className={`text-sm font-medium ${
            selectedRecipeIds.length === 3 ? "text-coral" : "text-muted-foreground"
          }`}>
            {selectedRecipeIds.length}/3 selected
          </span>
        </div>

        {/* Recipe Grid */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {recipes.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              isSelected={selectedRecipeIds.includes(recipe.id)}
              isDisabled={selectedRecipeIds.length >= 3 && !selectedRecipeIds.includes(recipe.id)}
              onClick={() => onRecipeSelect?.(recipe.id)}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Confidence Summary */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Average Match Confidence
            </span>
            <span className="text-lg font-bold text-green-600">
              {avgConfidence}%
            </span>
          </div>
          <Progress value={avgConfidence} className="h-2 bg-green-100" />
          <p className="text-xs text-muted-foreground">
            Based on your taste selection and recipe characteristics
          </p>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <Button
            onClick={(e) => {
              e.preventDefault();
              onSwipeClick?.(recipes.map((r) => r.id));
            }}
            className="h-10 text-sm font-medium"
          >
            <ChefHat className="mr-2 h-4 w-4" />
            Try Swiping
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              onDiscoverClick?.(recipes.map((r) => r.id));
            }}
            variant="outline"
            className="h-10 text-sm font-medium"
          >
            🔍 See All
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              onSwipeClick?.(recipes.map((r) => r.id));
            }}
            disabled={selectedRecipeIds.length !== 3}
            className="h-10 bg-coral text-sm font-medium text-white hover:bg-coral/90"
          >
            Done
          </Button>
        </div>

        {/* Info Text */}
        <p className="text-center text-xs text-muted-foreground">
          Swipe to save, rate, or add recipes to your meal plan
        </p>
      </CardContent>
    </Card>
  );
};

/**
 * Individual Recipe Card Component
 */
interface RecipeCardProps {
  recipe: any;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

const RecipeCard: React.FC<RecipeCardProps> = ({ recipe, isSelected, isDisabled, onClick }) => {
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`group relative flex h-full flex-col overflow-hidden rounded-lg border-2 transition-all duration-200 ${
        isSelected
          ? "border-coral bg-coral/5"
          : isDisabled
            ? "border-border/50 opacity-50 cursor-not-allowed"
            : "border-border bg-card hover:border-coral/50 hover:bg-accent"
      }`}
    >
      {/* Image Container */}
      <div className="relative aspect-video overflow-hidden bg-muted">
        {recipe.heroImageUrl ? (
          <img
            src={recipe.heroImageUrl}
            alt={recipe.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ChefHat className="h-6 w-6" />
          </div>
        )}

        {/* Confidence Badge */}
        <div className="absolute right-2 top-2 rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white">
          {recipe.matchConfidence}%
        </div>

        {/* Selected Checkmark */}
        {isSelected && (
          <div className="absolute inset-0 flex items-center justify-center bg-coral/20">
            <div className="rounded-full bg-coral p-2 text-white">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className="flex flex-1 flex-col space-y-2 p-3">
        {/* Title */}
        <h3 className="line-clamp-2 text-left text-sm font-semibold transition-colors group-hover:text-primary">
          {recipe.title}
        </h3>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-1 text-xs">
          {recipe.cuisine && (
            <Badge variant="secondary" className="text-xs">
              {recipe.cuisine}
            </Badge>
          )}
          {recipe.totalMinutes && (
            <Badge variant="outline" className="text-xs">
              {recipe.totalMinutes}m
            </Badge>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Match Info */}
        <div className="border-t border-border/50 pt-2">
          <p className="text-xs text-muted-foreground">
            ✨ Great match for you
          </p>
        </div>
      </div>
    </button>
  );
};
